import { Link } from "react-router-dom";
import { X, ArrowLeft } from "lucide-react";
import TypeaheadProductInput from "./TypeaheadProductInput";
import { RelationshipTypeDefinition } from "../types";

export interface RelationshipRowEntry {
  /** Stable key (backend id for persisted edges, synthetic for newly-added ones). */
  key: string;
  /** The "other" product on this relationship (not the current product). */
  otherProductId: string;
  otherProductName: string;
  /** True when the current product is the source of the stored edge (editable here). */
  isOutgoing: boolean;
  /** For read-only (incoming) edges: name of the product where this relationship is managed. */
  managedOnProductName?: string | undefined;
  managedOnProductId?: string | undefined;
  // Attribute fields are retained in the model so persisted values round-trip
  // through save without being wiped, even though they are no longer surfaced
  // in the UI.
  criticality?: string | undefined;
  since?: string | undefined;
  until?: string | undefined;
  notes?: string | undefined;
}

interface Props {
  typeDef: RelationshipTypeDefinition;
  entries: RelationshipRowEntry[];
  currentProductId: string;
  isEditing: boolean;
  canEdit: boolean;
  /**
   * Optional label to show instead of `typeDef.label`. Used by callers to
   * render the inverse perspective of an asymmetric relationship type.
   */
  labelOverride?: string;
  /**
   * When true, suppress the ArrowLeft glyph on incoming chips. Callers set
   * this for asymmetric split rows where direction is already encoded in the
   * row label.
   */
  hideDirectionGlyph?: boolean;
  /** Called with the new list of *outgoing* entries for this relationship type. */
  onOutgoingChange: (next: RelationshipRowEntry[]) => void;
}

export default function RelationshipPropertyRow({
  typeDef,
  entries,
  currentProductId,
  isEditing,
  canEdit,
  labelOverride,
  hideDirectionGlyph,
  onOutgoingChange,
}: Props) {
  const outgoing = entries.filter((e) => e.isOutgoing);
  const linkedProductIds = entries.map((e) => e.otherProductId);

  const handleAdd = (productId: string, productName: string) => {
    if (!productId) return;
    if (linkedProductIds.includes(productId)) return;
    const newEntry: RelationshipRowEntry = {
      key: `new:${productId}:${Date.now()}`,
      otherProductId: productId,
      otherProductName: productName,
      isOutgoing: true,
    };
    onOutgoingChange([...outgoing, newEntry]);
  };

  const handleRemove = (key: string) => {
    onOutgoingChange(outgoing.filter((e) => e.key !== key));
  };

  const rowEditable = isEditing && canEdit;

  return (
    <div>
      <label
        className="block text-sm font-medium text-gray-500 dark:text-gray-400"
        title={typeDef.description}
      >
        {labelOverride ?? typeDef.label}
      </label>

      {entries.length === 0 && !rowEditable && (
        <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
          Not specified
        </p>
      )}

      {entries.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {entries.map((entry) => {
            // Outgoing = filled primary; incoming = hollow with solid border
            // and an inverse-direction glyph (←) so direction is visible at a
            // glance.
            const shape = entry.isOutgoing
              ? "bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 text-primary-900 dark:text-primary-100 hover:bg-primary-100 dark:hover:bg-primary-900/30"
              : "bg-transparent border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50";

            return (
              <span
                key={entry.key}
                className={`inline-flex items-center rounded-md text-sm max-w-full ${shape}`}
              >
                <Link
                  to={`/products/${entry.otherProductId}`}
                  className="inline-flex items-center gap-2 px-2.5 py-1 min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-md"
                >
                  {!entry.isOutgoing && !hideDirectionGlyph && (
                    <ArrowLeft
                      className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    />
                  )}
                  <span className="font-medium truncate">
                    {entry.otherProductName}
                  </span>
                </Link>
                {rowEditable && entry.isOutgoing && (
                  <button
                    type="button"
                    onClick={() => handleRemove(entry.key)}
                    className="mr-1 rounded p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 focus-visible:ring-2 focus-visible:ring-red-400"
                    aria-label={`Remove ${entry.otherProductName}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {rowEditable && (
        <div className="mt-2">
          <TypeaheadProductInput
            value=""
            onChange={(pid, pname) => handleAdd(pid, pname)}
            excludeIds={[currentProductId, ...linkedProductIds].filter(Boolean)}
            placeholder="Find a product to link…"
          />
        </div>
      )}
    </div>
  );
}
